import { useCallback, useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface ModSettings {
  tributeReviewEnabled: boolean;
  pendingTributes: number;
  pendingArticles: number;
}

interface TributeItem {
  id: string;
  memorialId: string;
  memorialName: string;
  type: string;
  fromName: string;
  isAnonymous: boolean;
  message: string | null;
  status: string;
  createdAt: number;
}

interface ArticleItem {
  id: string;
  title: string;
  excerpt: string;
  scope: string;
  source: string;
  memorialId: string | null;
  memorialName: string;
  status: string;
  createdAt: number;
}

const TYPE_LABEL: Record<string, string> = {
  message: '留言',
  flower: '献花',
  candle: '点烛',
  incense: '上香',
};

function fmtTime(ts: number) {
  return new Date(ts * 1000).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 后台内容审核：留言与文章的待审队列，及留言审核开关 */
export default function AdminModeration() {
  const [sub, setSub] = useState<'tributes' | 'articles'>('tributes');
  const [settings, setSettings] = useState<ModSettings | null>(null);
  const [tributes, setTributes] = useState<TributeItem[]>([]);
  const [articles, setArticles] = useState<ArticleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const loadSettings = useCallback(async () => {
    const res = await api<ModSettings>('/admin/moderation/settings');
    if (res.ok && res.data) setSettings(res.data);
    else setError(errMsg(res, '加载审核设置失败'));
  }, []);

  const loadTributes = useCallback(async () => {
    const res = await api<{ items: TributeItem[] }>('/admin/moderation/tributes?status=pending');
    if (res.ok && res.data) setTributes(res.data.items);
  }, []);

  const loadArticles = useCallback(async () => {
    const res = await api<{ items: ArticleItem[] }>('/admin/moderation/articles?status=pending');
    if (res.ok && res.data) setArticles(res.data.items);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadSettings(), loadTributes(), loadArticles()]);
    setLoading(false);
  }, [loadSettings, loadTributes, loadArticles]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  async function decideTribute(id: string, action: 'approve' | 'reject') {
    setBusy(id + action);
    const res = await api(`/admin/moderation/tributes/${id}`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
    setBusy('');
    if (!res.ok) {
      setError(errMsg(res, '操作失败'));
      return;
    }
    setError('');
    await Promise.all([loadTributes(), loadSettings()]);
  }

  async function decideArticle(id: string, action: 'approve' | 'reject' | 'takedown') {
    setBusy(id + action);
    const res = await api(`/admin/moderation/articles/${id}`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
    setBusy('');
    if (!res.ok) {
      setError(errMsg(res, '操作失败'));
      return;
    }
    setError('');
    await Promise.all([loadArticles(), loadSettings()]);
  }

  async function toggleReview(next: boolean) {
    setBusy('toggle');
    const res = await api('/admin/moderation/settings', {
      method: 'PUT',
      body: JSON.stringify({ tributeReviewEnabled: next }),
    });
    setBusy('');
    if (!res.ok) {
      setError(errMsg(res, '切换失败'));
      return;
    }
    setError('');
    await loadSettings();
  }

  return (
    <div>
      {error && (
        <p className="mb-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
      )}

      {/* 审核开关 */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-ink-200 bg-white p-5">
        <div>
          <p className="text-sm font-medium text-ink-900">留言先审后公开</p>
          <p className="mt-1 text-xs text-ink-500">
            开启后，访客留言不会立即显示，需管理员在本页通过后才会出现在追思墙
          </p>
        </div>
        <button
          disabled={!settings || busy === 'toggle'}
          onClick={() => settings && toggleReview(!settings.tributeReviewEnabled)}
          className={`relative h-7 shrink-0 rounded-full transition disabled:opacity-40 ${
            settings?.tributeReviewEnabled ? 'bg-candle-500' : 'bg-ink-300'
          }`}
          style={{ width: 52 }}
        >
          <span
            className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${
              settings?.tributeReviewEnabled ? 'left-[26px]' : 'left-1'
            }`}
          />
        </button>
      </div>

      {/* 子栏目切换 */}
      <div className="mt-6 flex gap-2">
        {(
          [
            { id: 'tributes', label: '留言', n: settings?.pendingTributes ?? 0 },
            { id: 'articles', label: '文章', n: settings?.pendingArticles ?? 0 },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            className={`rounded-full px-4 py-1.5 text-xs transition ${
              sub === t.id
                ? 'bg-ink-900 text-ink-50'
                : 'border border-ink-200 bg-white text-ink-600 hover:bg-ink-100'
            }`}
          >
            {t.label}
            <span className="ml-1.5">{t.n}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-ink-500">加载中…</p>
      ) : sub === 'tributes' ? (
        tributes.length === 0 ? (
          <p className="mt-6 text-sm text-ink-500">没有待审留言。</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {tributes.map((t) => (
              <li key={t.id} className="rounded-2xl border border-ink-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
                  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-ink-600">
                    {TYPE_LABEL[t.type] ?? t.type}
                  </span>
                  <span>{t.fromName}</span>
                  <span>·</span>
                  <span>{t.memorialName}</span>
                  <span>·</span>
                  <span>{fmtTime(t.createdAt)}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink-800">
                  {t.message ?? '（无文字内容）'}
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    disabled={busy === t.id + 'approve'}
                    onClick={() => decideTribute(t.id, 'approve')}
                    className="h-9 rounded-xl bg-ink-900 px-5 text-xs text-ink-50 transition hover:bg-ink-800 disabled:opacity-40"
                  >
                    通过
                  </button>
                  <button
                    disabled={busy === t.id + 'reject'}
                    onClick={() => decideTribute(t.id, 'reject')}
                    className="h-9 rounded-xl border border-ink-300 px-5 text-xs text-ink-700 transition hover:bg-ink-100 disabled:opacity-40"
                  >
                    驳回
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : articles.length === 0 ? (
        <p className="mt-6 text-sm text-ink-500">没有待审文章。</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {articles.map((a) => (
            <li key={a.id} className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
                <span className="rounded-full bg-ink-100 px-2 py-0.5 text-ink-600">
                  {a.scope === 'platform' ? '平台栏目' : '馆内文章'}
                </span>
                {a.source === 'ai' && (
                  <span className="rounded-full bg-candle-300/40 px-2 py-0.5 text-candle-600">
                    AI 生成
                  </span>
                )}
                <span>{a.memorialName}</span>
                <span>·</span>
                <span>{fmtTime(a.createdAt)}</span>
              </div>
              <p className="mt-3 font-serifcn text-base font-medium text-ink-900">{a.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-ink-500">{a.excerpt}</p>
              <div className="mt-4 flex gap-2">
                <button
                  disabled={busy === a.id + 'approve'}
                  onClick={() => decideArticle(a.id, 'approve')}
                  className="h-9 rounded-xl bg-ink-900 px-5 text-xs text-ink-50 transition hover:bg-ink-800 disabled:opacity-40"
                >
                  发布
                </button>
                <button
                  disabled={busy === a.id + 'reject'}
                  onClick={() => decideArticle(a.id, 'reject')}
                  className="h-9 rounded-xl border border-ink-300 px-5 text-xs text-ink-700 transition hover:bg-ink-100 disabled:opacity-40"
                >
                  退回草稿
                </button>
                <button
                  disabled={busy === a.id + 'takedown'}
                  onClick={() => decideArticle(a.id, 'takedown')}
                  className="h-9 rounded-xl border border-red-200 px-5 text-xs text-red-600 transition hover:bg-red-50 disabled:opacity-40"
                >
                  下架
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
